import * as React from "react";
import { twMerge } from "tailwind-merge";
import type { TableColumn, TableProps } from "./types";
import Text from "../Text";
import { SVGs } from "../../assets";
import { Tools } from "../../utils";

export function ExpandHeaderCell() {
  return (
    <th className="w-10 border-b border-border px-2 py-3 text-left text-xs font-semibold text-text-primary/70" />
  );
}

export function EmptyRow({
  colSpan,
  emptyText,
}: {
  colSpan: number;
  emptyText: React.ReactNode;
}) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className="px-4 py-[100px] text-center text-sm text-text-primary/60"
      >
        {emptyText}
      </td>
    </tr>
  );
}

export function LoadingOverlay({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center">
      <SVGs.Spinner
        aria-label="Loading"
        className="h-6 w-6 animate-spin text-text-primary/70"
      />
    </div>
  );
}

export function TableScroller({
  scroll,
  children,
  containerRef,
}: {
  scroll: TableProps<any>["scroll"];
  children: React.ReactNode;
  containerRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const containerStyle: React.CSSProperties | undefined = scroll?.y
    ? { maxHeight: typeof scroll.y === "number" ? `${scroll.y}px` : scroll.y }
    : undefined;
  return (
    <div ref={containerRef} className="overflow-auto" style={containerStyle}>
      {children}
    </div>
  );
}

export function HeaderRow<T>({
  columns,
  onClick,
}: {
  columns: Array<TableColumn<T>>;
  onClick?: (column: TableColumn<T>) => void;
}) {
  return (
    <thead className="h-[47px] sticky top-0 z-10 bg-table-header">
      <tr>
        {columns.map((c, index) => (
          <th key={c.key} onClick={() => onClick?.(c)}>
            <div
              className={twMerge(
                "pl-[13px] flex justify-between items-center",
                c.className,
                c.classNames?.header,
              )}
            >
              <Text className="text-start" children={c.title} />
              <div className="flex items-center gap-[11px]">
                {c.sortable && (
                  <img src={SVGs.upDownCaret} alt="up-down-caret" />
                )}
                {index !== columns.length - 1 && (
                  <div className="h-[22px] w-[1px] bg-divider" />
                )}
              </div>
            </div>
          </th>
        ))}
      </tr>
    </thead>
  );
}

export function BodyRows<T>({
  rows,
  columns,
  rowKeyFn,
  expandable,
  expandedSet,
  canExpandRow,
  onToggleExpand,
  expandedRowRender,
}: {
  rows: T[];
  columns: Array<TableColumn<T>>;
  rowKeyFn: (row: T, index: number) => React.Key;
  expandable: boolean;
  expandedSet: Set<React.Key>;
  canExpandRow: (row: T) => boolean;
  onToggleExpand: (row: T, index: number) => void;
  expandedRowRender?: (row: T, index: number) => React.ReactNode;
}) {
  return (
    <tbody>
      {rows.map((row, idx) => {
        const key = rowKeyFn(row, idx);
        const canExpand = expandable ? canExpandRow(row) : false;
        const isExpanded = expandable ? expandedSet.has(key) : false;

        const toggleExpand = canExpand
          ? () => onToggleExpand(row, idx)
          : undefined;

        return (
          <React.Fragment key={String(key)}>
            <tr
              className={twMerge(
                expandable && "cursor-pointer hover:bg-table-header",
              )}
              onClick={() => expandable && toggleExpand?.()}
            >
              {columns.map((c) => {
                const value = Tools.getByPath(row, c.key as string) as string;
                return (
                  <td
                    key={c.key}
                    className={twMerge(
                      "align-top align-middle px-[16px]",
                      c.className,
                      c.classNames?.cell,
                    )}
                  >
                    {c.render?.(value, row, {
                      index: idx,
                      expanded: isExpanded,
                      toggleExpand,
                    }) ?? <Text className="text-start" children={value} />}
                  </td>
                );
              })}
            </tr>
            {expandable && canExpand && isExpanded && expandedRowRender ? (
              <tr className="bg-surface-2/20">
                <td colSpan={columns.length + 1}>
                  {expandedRowRender(row, idx)}
                </td>
              </tr>
            ) : null}
          </React.Fragment>
        );
      })}
    </tbody>
  );
}
